import { createApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { ListingDefaultsFormValues, ListingTemplate, ListingTemplateFormValues } from "./types";
import {
  transformListingDefaults,
  transformListingDefaultsOnUpdate,
  transformListingTemplate,
  transformListingTemplateOnUpdate,
} from "./transformations";

/*---------------------------------------------------------------------------------------
  LISTING DEFAULTS - GET
-----------------------------------------------------------------------------------------*/
export const getListingDefaultsAPI = async (): Promise<ListingDefaultsFormValues> => {
  const response = await createApiCall(
    { path: "/v1/settings/listing-defaults", method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return transformListingDefaults(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING DEFAULTS - UPDATE (upsert)
-----------------------------------------------------------------------------------------*/
export const updateListingDefaultsAPI = async (
  formValues: ListingDefaultsFormValues,
  t: (key: string) => string
): Promise<ListingDefaultsFormValues> => {
  const response = await createApiCall(
    {
      path: "/v1/settings/listing-defaults",
      method: "PUT",
      body: transformListingDefaultsOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.settings.listingdefaults.message.update"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformListingDefaults(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING TEMPLATES - SEARCH
-----------------------------------------------------------------------------------------*/
export const getListingTemplatesAPI = async (): Promise<ListingTemplate[]> => {
  const response = await createApiCall(
    { path: "/v1/settings/listing-templates", method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return (response.data ?? []).map(transformListingTemplate);
};

/*---------------------------------------------------------------------------------------
  LISTING TEMPLATES - GET
-----------------------------------------------------------------------------------------*/
export const getListingTemplateAPI = async (id: string): Promise<ListingTemplate> => {
  const response = await createApiCall(
    { path: `/v1/settings/listing-templates/${id}`, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return transformListingTemplate(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING TEMPLATES - CREATE
-----------------------------------------------------------------------------------------*/
export const createListingTemplateAPI = async (
  formValues: ListingTemplateFormValues,
  t: (key: string) => string
): Promise<ListingTemplate> => {
  const response = await createApiCall(
    {
      path: "/v1/settings/listing-templates",
      method: "POST",
      body: transformListingTemplateOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.settings.listingtemplate.message.create"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformListingTemplate(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING TEMPLATES - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateListingTemplateAPI = async (
  id: string,
  formValues: ListingTemplateFormValues,
  t: (key: string) => string
): Promise<ListingTemplate> => {
  const response = await createApiCall(
    {
      path: `/v1/settings/listing-templates/${id}`,
      method: "PUT",
      body: transformListingTemplateOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.settings.listingtemplate.message.update"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformListingTemplate(response.data);
};

/*---------------------------------------------------------------------------------------
  LISTING TEMPLATES - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteListingTemplateAPI = async (
  id: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    { path: `/v1/settings/listing-templates/${id}`, method: "DELETE" },
    {
      getMessage: {
        success: () => t("admin.settings.listingtemplate.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return !!response.data;
};
